import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AuthEntity } from '_entities';
import IsIncludes from 'modules/AccessControl/isIncludes';

const { getProfilePermissions, getModularityPermissions, getAccountModules } = AuthEntity.selectors;

/**
 * Visibility predicate for a nav item, shared by SubNav, SubNavMobile and the pages that also have to
 * bounce a deep link to a section the user can't see (`?activeTab=images` on an account without the
 * module).
 *
 * Two independent fields, both optional, ANDed when present:
 *   - `permission` — a ROLE permission, checked against the profile permissions.
 *   - `module` — an ACCOUNT module (`media_docs`, …). Modules arrive in `modularity_permissions` or
 *     `account_modules` depending on the endpoint, never in the profile permissions, so they get their
 *     own pool rather than being thrown at the `permission` check.
 *
 * Keeping them apart is what lets a section require "the account bought this AND this user may see it"
 * without the two collapsing into one pool where either alone would pass.
 */
const useNavItemAccess = () => {
  const userPermissions = useSelector(getProfilePermissions);
  const modularityPermissions = useSelector(getModularityPermissions);
  const accountModules = useSelector(getAccountModules);

  const modules = useMemo(
    () => [...new Set([...(modularityPermissions || []), ...(accountModules || [])])],
    [modularityPermissions, accountModules],
  );

  return useCallback(
    (item) => {
      if (!item) return true;
      if (item.permission && !IsIncludes(userPermissions, item.permission)) return false;
      if (item.module && !modules.includes(item.module)) return false;
      return true;
    },
    [userPermissions, modules],
  );
};

export default useNavItemAccess;
